import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import draftToHtml from 'draftjs-to-html';
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import { html } from 'js-beautify';
import { DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ModalButton, ModalDialogActions } from '../ModalComponents';
import ModalComponent from '../ModalComponent';
import styles from '../ModalComponent/modalStyles.module.css';

export default function ModalDiffViewer({ content }) {
  const [open, setOpen] = useState(false);
  const [formattedContent, setFormattedContent] = useState([]);

  const { t } = useTranslation();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (content) {
      try {
        const jsonDescription = JSON.parse(content);

        if (!Array.isArray(jsonDescription)) {
          console.warn('Content is not an array:', jsonDescription);
          setFormattedContent([]);
          return;
        }

        const formattedData = jsonDescription.map((item) => {
          const oldDesc = parseIfJson(item.values.old);
          const newDesc = parseIfJson(item.values.new);

          const convertContentToHtml = (content) => {
            try {
              const parsedContent = JSON.parse(content);

              if (typeof parsedContent === 'object' && parsedContent.blocks) {
                const editorStateFromContent = EditorState.createWithContent(
                  convertFromRaw(parsedContent)
                );
                const rawContentState = convertToRaw(editorStateFromContent.getCurrentContent());
                return draftToHtml(rawContentState);
              } else {
                return content;
              }
            } catch (e) {
              return content;
            }
          };

          return {
            key: item.key,
            oldValue: convertContentToHtml(oldDesc).toString(),
            newValue: convertContentToHtml(newDesc).toString(),
          };
        });

        setFormattedContent(formattedData);
      } catch (error) {
        console.error('Error parsing audit log content:', error);
        console.log('Content that caused the error:', content);
        setFormattedContent([]);
      }
    } else {
      console.log('No content provided to ModalDiffViewer');
      setFormattedContent([]);
    }
  }, [content]);

  const parseIfJson = (str) => {
    try {
      return JSON.parse(str);
    } catch (e) {
      return str;
    }
  };

  const htmlStylingOptions = {
    indent_size: 4,
    html: {
      end_with_newline: true,
    },
  };

  function formatHTMLForDiff(htmlStr, textOnly) {
    const formattedHTML = html(htmlStr, htmlStylingOptions);
    if (textOnly) {
      return new DOMParser().parseFromString(formattedHTML, 'text/html').body.innerText;
    }

    return formattedHTML;
  }

  const compareStyles = {
    variables: {
      light: {
        codeFoldGutterBackground: '#6F767E',
        codeFoldBackground: '#E2E4E5',
      },
    },
  };
  return (
    <div>
      <ModalButton
        fullWidth
        onClick={handleOpen}
        variant="text"
        sx={{
          fontSize: '14px',
          textTransform: 'none',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          '&:hover': {
            borderColor: 'rgba(0, 0, 0, 1)',
            background: 'none',
          },
        }}
      >
        {t('modals.clickView')}
      </ModalButton>
      <ModalComponent open={open} handleClose={handleClose}>
        <div className={styles.modal}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {t('generic.auditLogs')} - {t('modals.clickView')}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers sx={{ p: 3 }}>
            {formattedContent.length > 0 ? (
              formattedContent.map((item, index) => (
                <div key={index} style={{ marginBottom: '24px' }}>
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      color: 'var(--text-primary)',
                    }}
                  >
                    {`${item.key}`}
                  </h3>
                  <ReactDiffViewer
                    oldValue={formatHTMLForDiff(item.oldValue, true)}
                    newValue={formatHTMLForDiff(item.newValue, true)}
                    splitView={true}
                    compareMethod={DiffMethod.WORDS}
                    styles={compareStyles}
                    leftTitle={t('modals.initialVersion')}
                    rightTitle={t('modals.adjustedSolution')}
                    renderContent={(source) => {
                      return source;
                    }}
                  />
                </div>
              ))
            ) : (
              <div
                style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  color: 'var(--text-secondary)',
                }}
              >
                <p>No data to display</p>
                <p style={{ marginTop: '8px', fontSize: '14px' }}>
                  Content: {content ? 'Provided' : 'Not provided'}
                </p>
              </div>
            )}
          </DialogContent>

          <ModalDialogActions>
            <ModalButton variant="outlined" color="primary" onClick={handleClose}>
              {t('modals.close')}
            </ModalButton>
          </ModalDialogActions>
        </div>
      </ModalComponent>
    </div>
  );
}
